/**
 * Automatically adds/removes a free gift-with-purchase variant when a specific "trigger" variant
 * is added to/removed from the cart. Relies on a cart/checkout discount already configured to make
 * the gift variant free when the trigger variant is present - this script only handles adding the
 * physical line item, not the pricing.
 *
 * The theme's "After add to cart" setting is set to "Redirect to cart page" (see
 * settings.after_add_to_cart / assets/product-form.js), which navigates the whole page away ~300ms
 * after a successful add - so the gift needs to be added fast, and reliably, inside that window.
 *
 * Firing a second, independent /cart/add.js request at the same time as the trigger product's own
 * request only works once a cart session already exists. A brand new visitor has no cart yet, and a
 * plain GET to /cart.js doesn't create/persist one (there's nothing to persist for an empty cart) -
 * so two concurrent POSTs can each try to be the request that establishes the cart, and only one of
 * them "wins" the session cookie; the other's item is silently lost. This is why it only affected
 * new customers, and only on their first attempt (by the second attempt a cart already exists).
 *
 * To avoid the race entirely, this wraps the global `fetch` to detect the exact moment the trigger
 * product's own add-to-cart request (made by assets/product-form.js) succeeds, and only then fires
 * the gift's request - by which point the cart is guaranteed to already exist, because the
 * trigger's own add just created/confirmed it. `keepalive` ensures the request still completes if
 * the page navigates away before it resolves.
 */
(function () {
  var TRIGGER_VARIANT_ID = 54918632866165; // Luxury subscription
  var GIFT_VARIANT_ID = 57342876811637; // Free vase (product 15922133696885's only variant, "Default Title")

  var originalFetch = window.fetch;

  /**
   * Checks whether a cart object contains a given variant.
   * @param {object} cart - Cart object (as returned by the Cart AJAX API).
   * @param {number} variantId - Variant id to look for.
   * @returns {boolean}
   */
  function cartHasVariant(cart, variantId) {
    return cart.items.some(function (item) {
      return item.variant_id === variantId;
    });
  }

  /**
   * Asks the cart drawer (if present) to re-render itself to reflect the latest cart state.
   */
  function refreshCartUi() {
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
  }

  /**
   * Adds the gift variant to the cart, unless it's already there. Uses keepalive so the request
   * completes even if the page navigates away (e.g. "After add to cart" redirecting to the cart
   * page) before it resolves.
   */
  function addGiftIfMissing() {
    originalFetch(`${theme.routes.cart}.js`)
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        if (cartHasVariant(cart, GIFT_VARIANT_ID)) return null;

        return originalFetch(theme.routes.cartAdd, {
          method: 'POST',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ items: [{ id: GIFT_VARIANT_ID, quantity: 1 }] })
        });
      })
      .then(function (response) {
        if (response) refreshCartUi();
      })
      .catch(function (error) {
        console.log(error); // eslint-disable-line
      });
  }

  /**
   * Silently removes the gift variant from the cart.
   */
  function removeGift() {
    originalFetch(theme.routes.cartChange, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ id: GIFT_VARIANT_ID, quantity: 0 })
    })
      .then(refreshCartUi)
      .catch(function (error) {
        console.log(error); // eslint-disable-line
      });
  }

  /**
   * Checks whether a fetch request body (a FormData instance, as built by product-form.js) is
   * adding the trigger variant.
   * @param {*} body - The `init.body` passed to fetch.
   * @returns {boolean}
   */
  function bodyIsTriggerAdd(body) {
    return body instanceof FormData && Number(body.get('id')) === TRIGGER_VARIANT_ID;
  }

  // Wrap the global fetch so we can react the instant the trigger product's own add-to-cart
  // request (made elsewhere, by assets/product-form.js) succeeds - see comment above for why.
  window.fetch = function (input, init) {
    var isTriggerAdd = input === theme.routes.cartAdd && init && bodyIsTriggerAdd(init.body);
    var fetchPromise = originalFetch.apply(window, arguments);

    if (isTriggerAdd) {
      fetchPromise.then(function (response) {
        if (response.ok) addGiftIfMissing();
      });
    }

    return fetchPromise;
  };

  document.addEventListener('on:line-item:change', function (evt) {
    if (evt.detail.variantId !== TRIGGER_VARIANT_ID || evt.detail.newQuantity !== 0) return;
    if (!cartHasVariant(evt.detail.cart, GIFT_VARIANT_ID)) return;

    removeGift();
  });
})();
